import React, { useEffect } from 'react';
import { Shield, Lock, Eye, UserCheck, Database, Mail } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

/**
 * Privacy Policy Page for Sunrise Bookstore
 * Covers data collection, usage, security, and user rights
 */

const PrivacyPolicy: React.FC = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <main className="min-h-screen bg-background text-text font-sans antialiased">
      <Helmet>
        <title>Privacy Policy | Sunrise Bookstore</title>
        <meta name="description" content="Learn how Sunrise Bookstore collects, uses, and protects your personal information." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-50 to-background py-16">
        <div className="container-custom max-w-5xl mx-auto px-4">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-6">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-primary-800 max-w-2xl mx-auto">
              Your privacy matters to us. Learn how we collect, use, and protect your information.
            </p>
            <p className="text-sm text-primary-600 mt-4">
              Last updated: December 25, 2025
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container-custom max-w-4xl mx-auto px-4">
          
          {/* Introduction */}
          <div className="mb-12">
            <p className="text-lg text-primary-900 leading-relaxed">
              At <strong>Sunrise Bookstore</strong>, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your data when you visit our website or make a purchase.
            </p>
          </div>

          {/* Information We Collect */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <Database className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-primary">1. Information We Collect</h2>
            </div>
            
            <div className="space-y-4 pl-13">
              <div>
                <h3 className="text-xl font-semibold text-primary-900 mb-2">Personal Information</h3>
                <ul className="list-disc list-inside space-y-2 text-primary-800">
                  <li>Name, email address, and phone number</li>
                  <li>Shipping and billing addresses</li>
                  <li>Payment information (processed securely through third-party payment processors)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* How We Use Your Information */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-primary">2. How We Use Your Information</h2>
            </div>
            
            <div className="space-y-3 pl-13 text-primary-800">
              <p>We use the information we collect to:</p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Process orders:</strong> Fulfill your purchases, manage payments, and arrange shipping</li>
                <li><strong>Communicate:</strong> Send order confirmations, shipping updates, and customer support responses</li>
                {/* <li><strong>Improve our service:</strong> Analyze user behavior to enhance website functionality and user experience</li> */}
                {/* <li><strong>Marketing:</strong> Send promotional emails (you can opt-out at any time)</li> */}
                <li><strong>Security:</strong> Prevent fraud and unauthorized access</li>
                <li><strong>Legal compliance:</strong> Comply with applicable laws and regulations</li>
              </ul>
            </div>
          </div>

          {/* Information Sharing */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <Eye className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-primary">3. Information Sharing</h2>
            </div>
            
            <div className="space-y-3 pl-13 text-primary-800">
              <p>We do not sell your personal information. We may share your data with:</p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Service providers:</strong> Shipping companies, payment processors, and cloud hosting services</li>
                <li><strong>Legal authorities:</strong> When required by law or to protect our rights</li>
                <li><strong>Business transfers:</strong> In case of merger, acquisition, or sale of assets</li>
              </ul>
              <p className="mt-3 text-sm italic">All third parties are contractually obligated to protect your information.</p>
            </div>
          </div>

          {/* Data Security */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <Lock className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-primary">4. Data Security</h2>
            </div>
            
            <div className="space-y-3 pl-13 text-primary-800">
              <p>We implement industry-standard security measures to protect your information:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>SSL/TLS encryption for data transmission</li>
                <li>Secure servers with restricted access</li>
                <li>Regular security audits and vulnerability assessments</li>
                {/* <li>Encrypted storage of sensitive data</li> */}
              </ul>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-4">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> While we strive to protect your data, no method of transmission over the internet is 100% secure. Use strong passwords and keep your login credentials confidential.
                </p>
              </div>
            </div>
          </div>

          {/* Your Rights */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-primary">5. Your Rights</h2>
            </div>
            
            <div className="space-y-3 pl-13 text-primary-800">
              <p>You have the right to:</p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your account and personal data</li>
                {/* <li><strong>Opt-out:</strong> Unsubscribe from marketing emails</li> */}
                <li><strong>Data portability:</strong> Receive your data in a structured, machine-readable format</li>
              </ul>
              <p className="mt-3">
                To exercise these rights, contact us at{' '}
                <a href="mailto:sunrisephotostate@gmail.com" className="text-primary hover:underline font-semibold">
                  sunrisephotostate@gmail.com
                </a>
              </p>
            </div>
          </div>

          {/* Cookies
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-primary mb-4">6. Cookies and Tracking</h2>
            
            <div className="space-y-3 pl-13 text-primary-800">
              <p>We use cookies and similar technologies to:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Remember your preferences and login status</li>
                <li>Analyze site traffic and user behavior</li>
                <li>Personalize content and advertisements</li>
              </ul>
              <p className="mt-3">
                You can manage cookie preferences through your browser settings. Note that disabling cookies may affect website functionality.
              </p>
            </div>
          </div> */}

          {/* Third-Party Links */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-primary mb-4">6. Third-Party Links</h2>
            <div className="pl-13 text-primary-800">
              <p>
                Our website may contain links to third-party sites. We are not responsible for their privacy practices. Please review their privacy policies before providing any personal information.
              </p>
            </div>
          </div>

          {/* Children's Privacy */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-primary mb-4">7. Children's Privacy</h2>
            <div className="pl-13 text-primary-800">
              <p>
                Our service can be used by individuals under 18 with parental consent. We do not knowingly collect personal information from children without such consent. If you believe we have collected data from a child without consent, please contact us to request deletion.
              </p>
            </div>
          </div>

          {/* Changes to Policy */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-primary mb-4">8. Changes to This Policy</h2>
            <div className="pl-13 text-primary-800">
              <p>
                We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on this page and updating the "Last updated" date. Continued use of our services after changes constitutes acceptance of the updated policy.
              </p>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-8 mt-12">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-primary mb-3">Questions About Privacy?</h2>
                <p className="text-primary-800 mb-4">
                  If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="space-y-2 text-primary-900">
                  <p><strong>Email:</strong> <a href="mailto:sunrisephotostate@gmail.com" className="text-primary hover:underline">sunrisephotostate@gmail.com</a></p>
                  <p><strong>Phone:</strong> +91 9460999000</p>
                  <p><strong>Address:</strong> Sunrise Bookstore, 8V9X+GCX, Court Rd, Swami Vivekananda Nagar, Suratgarh, Rajasthan 335804</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>
    </main>
  );
};

export default PrivacyPolicy;
